import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import {BreakpointObserver} from '@angular/cdk/layout';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {ToolsService} from '../../../../services/tools.service';
import {UrlBuilderService} from '../../../../services/url-builder.service';
import {MapData} from '../../../../types/MapData';
import {MapInfoWindow, MapMarker} from '@angular/google-maps';
import {CodeSnippetsService} from '../../../../services/code-snippets.service';
import {CareproviderMapItemTranslated} from '../../../../types/CareproviderMapItemTranslated';
import {Subscription} from 'rxjs';
import {MapsService} from '../../../../services/maps.service';
import {CareproviderMapItem} from '../../../../types/CareproviderMapItem';
import {ScriptNameEnum} from '../../../../enums/SsciptsEnums';
import {AppConfigService} from '../../../../services/app-config.service';
import {SearchService} from '../../../../services/search/search.service';
import {isPlatformBrowser} from '@angular/common';

@Component({
  selector: 'app-google-map',
  templateUrl: './google-map-modal.component.html',
  styleUrls: ['./google-map-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GoogleMapModalComponent implements OnInit, OnDestroy {
  public markers = signal<CareproviderMapItemTranslated[]>([]);
  public clickedMarker = signal<CareproviderMapItemTranslated>(null);
  public options: google.maps.MapOptions;
  public infoContent = signal<string>(null);
  public removeEventListener: () => void;
  @ViewChild(MapInfoWindow, {static: false}) info!: MapInfoWindow;
  public isFirstMarkerLoaded = signal<boolean>(false);
  private subscriptions: Subscription[] = [];
  private isMapInitialized = signal<boolean>(false);
  private isStaticMarkersMode = signal<boolean>(false);
  private map: google.maps.Map;
  private bounds: google.maps.LatLngBounds;
  private centerLat = signal<number>(null);
  private centerLng = signal<number>(null);
  private debouncedBoundsChangeHandleFunction: Function;
  private codeSnippets = inject(CodeSnippetsService);
  private loadedScripts = this.codeSnippets.loadedScripts;
  public isMapScriptLoaded = computed<boolean>(
    () => this.loadedScripts() && this.loadedScripts()[ScriptNameEnum.GOOGLE_MAPS_CLUSTERER] === true,
  );
  private searchService = inject(SearchService);
  public searchState = this.searchService.searchState;
  private userLocationState = this.searchService.userLocationState;

  constructor(
    private urlBuilder: UrlBuilderService,
    private tools: ToolsService,
    @Inject(MAT_DIALOG_DATA) public data: MapData,
    private dialogRef: MatDialogRef<GoogleMapModalComponent>,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private renderer: Renderer2,
    private mapsService: MapsService,
    private appConfigService: AppConfigService,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.debouncedBoundsChangeHandleFunction = this.tools.debounce(
      () => {
        if (this.isStaticMarkersMode() === false) {
          if (this.isFirstMarkerLoaded() === true) {
            const center = this.map.getCenter();
            if (this.tools.isLocationInSwedenArea(center.lat(), center.lng())) {
              this.bounds = this.map.getBounds();
              this.reloadMarkers();
            }
          }
        }
      },
      300,
      false,
    );

    effect(
      () => {
        if (this.searchState() && this.userLocationState() && this.isStaticMarkersMode() === false) {
          this.reloadMarkers();
        }
      },
      {allowSignalWrites: true},
    );
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    this.closeDialog();
  }

  ngOnInit(): void {
    let center: google.maps.LatLngLiteral = null;
    if (!this.tools.isNil(this.data) && !this.tools.isNil(this.data.markers) && this.data.markers.length > 0) {
      this.data.markers.forEach((marker) => {
        this.createMarker(marker);
      });
      this.clickedMarker.set(this.markers()[0]);
      center = {
        lat: this.clickedMarker().location.lat,
        lng: this.clickedMarker().location.lng,
      };
      this.isStaticMarkersMode.set(true);
      this.isFirstMarkerLoaded.set(true);
    } else {
      this.isStaticMarkersMode.set(false);
    }

    if (isPlatformBrowser(this.platformId)) {
      this.loadMapScripts();
    }

    this.options = {
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      maxZoom: 17,
      zoom: 13,
      center: center,
      styles: [
        {
          featureType: 'poi',
          stylers: [{visibility: 'off'}],
        },
      ],
    };

    if (this.breakpointObserver.isMatched(['(max-width: 768px)'])) {
      this.options.zoomControl = false;
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public onMarkerClick(mapMarker: MapMarker, marker: CareproviderMapItemTranslated): void {
    if (this.removeEventListener) {
      this.removeEventListener();
    }
    this.clickedMarker.set(marker);
    this.setInfoWindowContent();
    this.info.open(mapMarker);
  }

  public onMapClick(event: any): void {
    this.closeInfoWindow();
  }

  public closeInfoWindow(): void {
    if (!this.tools.isNil(this.info)) {
      this.info.close();
    }
  }

  public closeDialog(): void {
    if (this.removeEventListener) {
      this.removeEventListener();
    }
    this.closeInfoWindow();
    this.dialogRef.close();
  }

  public navigateTo(event: Event) {
    event.preventDefault();
    if (this.removeEventListener) {
      this.removeEventListener();
    }
    this.closeDialog();
    this.router.navigate(this.clickedMarker().routerLink);
  }

  public onMapInitialized(map: google.maps.Map): void {
    this.map = map;
    this.isMapInitialized.set(true);
    this.setMapCenterOnFirstMarkerOrUserLocation();
  }

  public onBoundsChanged(): void {
    this.debouncedBoundsChangeHandleFunction();
  }

  private setMapCenterOnFirstMarkerOrUserLocation(): void {
    if (this.markers().length > 0 && this.centerLat() === null && this.centerLng() === null) {
      if (this.isStaticMarkersMode() === true || this.isFirstMarkerLoaded() === false) {
        this.centerLat.set(this.markers()[0].location.lat);
        this.centerLng.set(this.markers()[0].location.lng);
      } else if (this.searchState() && this.searchState().locationSearch === true) {
        this.centerLat.set(this.searchState().lat);
        this.centerLng.set(this.searchState().lon);
      }
    }

    if (this.isMapInitialized() && this.centerLat() !== 0 && this.centerLng() !== 0) {
      this.map.setCenter({
        lat: this.centerLat(),
        lng: this.centerLng(),
      });
    }
  }

  private createMarker(marker: CareproviderMapItemTranslated): void {
    if (marker && marker.location && marker.location.lat !== 0 && marker.location.lng !== 0) {
      const markerOptions: google.maps.MarkerOptions = {
        draggable: false,
        clickable: true,
        opacity: 1,
      };
      const addMarker: CareproviderMapItemTranslated = {...marker};
      addMarker.routerLink = this.getUrl(marker);
      addMarker.imageUrl = this.getImg(marker);
      addMarker.title = marker.id.toString();
      addMarker.options = markerOptions;
      addMarker.address.visitingAddress =
        !this.tools.isNil(marker.address.visitingAddress) || marker.address.visitingAddress === ''
          ? marker.address.street + ' ' + marker.address.city
          : marker.address.visitingAddress;

      this.markers.update((markers) => [...markers, addMarker]);
    }
  }

  private getImg(marker: CareproviderMapItemTranslated) {
    let imgUrl = '';
    if (!this.tools.isNil(marker.thumbnail) && marker.thumbnail !== '') {
      imgUrl = `${this.appConfigService.appConfig.amazonS3Url}${marker.thumbnail}`;
    }
    return imgUrl;
  }

  private getUrl(marker: CareproviderMapItemTranslated): string[] {
    const routerLink: string[] = this.urlBuilder.CreateCareproviderUnitRouterLink(marker.id, marker.urlPath);
    return routerLink;
  }

  private setInfoWindowContent(): void {
    this.infoContent.set(
      `<div class="info-window-content">` +
        `<a class="inline-block" href="${this.urlBuilder.CreateStaticLink(this.clickedMarker().routerLink)}" title="${
          this.clickedMarker().name
        }">` +
        `${this.clickedMarker().name}</a><em>${this.clickedMarker().address.visitingAddress}</em><br>`,
    );
    if (
      this.clickedMarker().imageUrl !== null &&
      this.clickedMarker().imageUrl !== '' &&
      this.clickedMarker().imageUrl !== 'null'
    ) {
      this.infoContent.update(
        (content) =>
          content +
          `<a class="inline-block" href="${this.urlBuilder.CreateStaticLink(this.clickedMarker().routerLink)}" title="${
            this.clickedMarker().name
          }">` +
          `<img src="${this.clickedMarker().imageUrl}" alt="clicked marker"></a>`,
      );
    }

    this.infoContent.update((content) => content + `</div>`);
  }

  private reloadMarkers() {
    if (this.searchState() && this.userLocationState()) {
      this.subscriptions.push(
        this.mapsService
          .GetSearchMapResults(this.searchState(), this.userLocationState(), this.bounds)
          .subscribe((result) => this.loadMarkers(result)),
      );
    }
  }

  private loadMarkers(searchResults: CareproviderMapItem[]): void {
    this.markers.set([]);
    searchResults.forEach((item) => {
      const translatedMarker: CareproviderMapItemTranslated = {
        id: item.id,
        name: item.name,
        urlPath: item.urlPath,
        location: {
          lat: item.location.lat,
          lng: item.location.lon,
        },
        rating: item.rating,
        address: item.address,
        thumbnail: item.thumbnail,
      };
      this.createMarker(translatedMarker);
    });
    if (this.isFirstMarkerLoaded() === false) {
      this.setMapCenterOnFirstMarkerOrUserLocation();
    }
    this.isFirstMarkerLoaded.set(true);
  }

  private loadMapScripts(): void {
    const mapsClustererScript: string = 'https://unpkg.com/@googlemaps/markerclustererplus/dist/index.min.js';
    this.codeSnippets.loadExternalScript(this.renderer, mapsClustererScript, ScriptNameEnum.GOOGLE_MAPS_CLUSTERER);
  }
}
